import { useParams } from 'react-router-dom';
import { Tile } from 'components/Layout/PageContainer';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useEffect } from 'react';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { BreadcrumbItem } from 'components/breadcrumb/breadcrumbModel';
import { useArticleSummary } from 'pages/entities/queryHooks';
import { getErrorMessage } from 'utils/getErrorMessage';
import { EntityPage } from 'pages/entities/components/EntityPage';
import { Filters } from 'services/data.service';
import { useModelService } from 'services/model/model.service';
import { ArticleDetailsSummary } from 'pages/entities/article/ArticleDetailsSummary';
import { createArticleNameFromAttributes } from 'pages/entities/entityhelper';

export function ArticleEntityPage() {
  const { articleEAN } = useParams();
  const { getAttributes } = useModelService();

  // Fetch product attributes without sales-dimension filters so the summary
  // tile always shows product info (name, colors, sizes, etc.) even when the
  // filtered sales data returns no results
  const { data: summaryResponse, error: summaryError } = useArticleSummary(articleEAN ?? '');

  const attributes = summaryResponse?.attributes ?? {};
  const label = createArticleNameFromAttributes(attributes);

  // replace the product number in breadcrumb by the product name when data is loaded
  useEffect(() => {
    if (summaryResponse && articleEAN) {
      const { breadcrumbs, setBreadcrumbs } = useBreadcrumbStore.getState();

      const updateProductBreadcrumb = (items: BreadcrumbItem[]): BreadcrumbItem[] =>
        items.map(item => {
          if (item.path?.includes(articleEAN)) {
            return {
              ...item,
              label: label,
            };
          }

          return item;
        });

      const updatedBreadcrumbs = updateProductBreadcrumb(breadcrumbs);
      setBreadcrumbs(updatedBreadcrumbs);
    }
  }, [summaryResponse, articleEAN]);

  if (!summaryResponse && !summaryError) {
    return <MetricsLoadingSpinner />;
  }

  const filterDecorator = (filters: Filters) => {
    return { ...filters, articleEANs: [articleEAN] } as Filters;
  };

  const availableTables = getAttributes(['chnMarketplace', 'chnCountry']);

  return (
    <EntityPage
      filterDecorator={filterDecorator}
      disabledFilters={[
        'prdNumber',
        'prdCategory',
        'prdGender',
        'prdTargetAge',
        'prdSeason',
        'styBrandColor',
        'artSize',
      ]}
      availableTables={availableTables}
    >
      {summaryError ? (
        <Tile title={articleEAN} errorDetails={getErrorMessage(summaryError)} />
      ) : (
        <ArticleDetailsSummary response={summaryResponse} />
      )}
    </EntityPage>
  );
}
